import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface VersionInfo {
  version: string;
  buildTime: string;
}

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  // Check every 30 minutes
  // This reduces network overhead while still catching updates
  // Event-based checks (focus/visibility) ensure updates are detected when user returns
  private readonly CHECK_INTERVAL = 30 * 60 * 1000; // Check every 30 minutes
  private readonly VERSION_FILE = 'version.json';
  private currentVersion$ = new BehaviorSubject<string>('');
  private isNewVersionAvailable$ = new BehaviorSubject<boolean>(false);
  private intervalSubscription?: Subscription;
  private versionLoaded = false; // Track if version has been loaded

  constructor(private http: HttpClient) {
    // Version check deferred to startVersionCheck() to avoid blocking app initialization
  }

  /**
   * Load the current version from the version.json file
   */
  private loadCurrentVersion(): void {
    // Add timestamp to prevent caching
    const url = `/${this.VERSION_FILE}?t=${Date.now()}`;
    
    this.http.get<VersionInfo>(url).pipe(
      catchError(() => {
        // If version.json doesn't exist, use a fallback
        return of({ version: this.getBuildTimestamp(), buildTime: new Date().toISOString() });
      })
    ).subscribe((versionInfo) => {
      const storedVersion = localStorage.getItem('app_version');
      
      if (storedVersion && storedVersion !== versionInfo.version) {
        // New version detected
        this.isNewVersionAvailable$.next(true);
      } else if (!storedVersion) {
        // First time loading, store the version
        localStorage.setItem('app_version', versionInfo.version);
      }
      
      this.currentVersion$.next(versionInfo.version);
    });
  }

  /**
   * Start periodic version checking
   * Checks on initial load, periodically, and when user becomes active
   */
  startVersionCheck(): void {
    // Only run in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Load current version first (deferred from constructor)
    if (!this.versionLoaded) {
      this.loadCurrentVersion();
      this.versionLoaded = true;
    }

    // Check immediately on app load
    this.checkVersion();
    
    // Check periodically (every 30 minutes)
    this.intervalSubscription = interval(this.CHECK_INTERVAL).subscribe(() => {
      this.checkVersion();
    });

    // Check when user returns to the tab/window (more efficient than constant polling)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          // User returned to the tab, check for updates
          this.checkVersion();
        }
      });

      // Check when window gains focus (user switches back to the app)
      window.addEventListener('focus', () => {
        this.checkVersion();
      });
    }
  }

  /**
   * Stop version checking (cleanup)
   */
  stopVersionCheck(): void {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
  }

  /**
   * Check for new version
   */
  private checkVersion(): void {
    const url = `/${this.VERSION_FILE}?t=${Date.now()}`;
    
    this.http.get<VersionInfo>(url).pipe(
      catchError(() => {
        // Silently fail if version.json doesn't exist
        return of(null);
      })
    ).subscribe((versionInfo) => {
      if (versionInfo && versionInfo.version) {
        const storedVersion = localStorage.getItem('app_version');
        
        if (storedVersion && storedVersion !== versionInfo.version) {
          // New version available
          this.isNewVersionAvailable$.next(true);
        }
      }
    });
  }

  /**
   * Get observable for new version availability
   */
  getIsNewVersionAvailable(): Observable<boolean> {
    return this.isNewVersionAvailable$.asObservable();
  }

  /**
   * Get current version
   */
  getCurrentVersion(): Observable<string> {
    return this.currentVersion$.asObservable();
  }

  /**
   * Refresh the application
   */
  refreshApplication(): void {
    // Only run in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Clear localStorage version and notified version to allow fresh start after refresh
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('app_version');
      localStorage.removeItem('app_notified_version');
    }
    
    // Helper function to reload
    const reload = () => {
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    };
    
    // Clear cache and reload
    if (typeof caches !== 'undefined' && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
        // Reload after cache is cleared
        reload();
      }).catch(() => {
        // If cache clearing fails, still reload
        reload();
      });
    } else {
      // Reload if cache API is not available
      reload();
    }
  }

  /**
   * Get build timestamp as fallback version
   */
  private getBuildTimestamp(): string {
    // This will be replaced during build with actual timestamp
    return new Date().toISOString();
  }
}
